import { CommonModule } from '@angular/common';
import {  Component, } from '@angular/core';
import { Router } from '@angular/router';
import { Store, createSelector } from '@ngrx/store';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';

import { TransactionStage } from '../../../generated/graphql.generated';
import {
  selectMayRefundTransaction,
  selectMayTakeBlueSageCardPayment,
} from '../../bluesage/bluesage-job.selectors';
import {
  BLUESAGE_COMPLETING_HOLD_DELETE_REASON,
  BLUESAGE_OPEN_HOLD_DELETE_REASON,
  BLUESAGE_OPEN_PAY_LINK_DELETE_REASON,
  BLUESAGE_REFUND_IN_PROGRESS_REASON,
  blueSageRefundInProgress,
  describeBlueSagePendingReason,
  describeBlueSageRail,
  describeBlueSageRefundFailure,
  hasOpenBlueSagePayLink,
} from '../../bluesage/bluesage.util';
import { BlueSageRefundDialogActions } from '../../bluesage/refund-dialog/bluesage-refund-dialog.actions';
import { BlueSageRefundDialogComponent } from '../../bluesage/refund-dialog/bluesage-refund-dialog.component';
import { describeWhyNotRefundable, refundableMinorOf } from '../../bluesage/refund-dialog/bluesage-refund-dialog.util';
import { DetailsHelperService } from '../../services/details-helper.service';
import { SharedModule } from '../../shared/shared.module';
import { createCheckSinglePermissionSelector } from '../../state/branding.store';
import { CancelPayLinkDialogActions } from '../../transactions/cancel-pay-link-dialog/cancel-pay-link-dialog.actions';
import { CancelPreauthDialogActions } from '../../transactions/cancel-preauth-dialog/cancel-preauth-dialog.actions';
import { CreateTransactionDialogActions } from '../../transactions/create-transaction-dialog/create-transaction-dialog.actions';
import { selectJobCollectibles } from '../../transactions/transaction-job.selectors';
import { TagColor } from '../job-estimate/job-estimate.component';
import { JobInvoiceComponent } from '../job-invoice/job-invoice.component';
import { OverviewSummaryComponent } from '../job-overview/overview-summary/overview-summary.component';
import {
  jobToolFeature,
  selectJob,
  selectJobInCurrentZone,
  selectLatestInvoice,
  selectOlderInvoices,
  selectTransactionsWithInvoiceNames,
  selectInvoices,
  selectTransactions,
  selectJobLoaded,
} from '../job-tool.reducer';
import { selectJobTotals } from '../job-workorders/job-workorders.component';
import { selectDamages } from '../job-workorders/job-workorders.selectors';


import { JobFinancialsActions } from './job-financials.actions';


@Component({
  selector: 'app-job-financials',
  standalone: true,
  imports: [
    CommonModule,
    FreyaCommonModule,
    SharedModule,
    JobInvoiceComponent,
    OverviewSummaryComponent,
    BlueSageRefundDialogComponent,
  ],
  templateUrl: './job-financials.component.html',
  styleUrls: [
    './job-financials.component.scss',
    './document-container-style.scss',
  ],
})
export class JobFinancialsComponent {

  private storeViewModel$ = this.store.select(createSelector({
    jobLoaded: selectJob,
    latestInvoice: selectLatestInvoice,
    olderInvoices: selectOlderInvoices,
    transactions: selectTransactionsWithInvoiceNames,
    damages: selectDamages,
    invoicesEmpty: createSelector(
      selectInvoices,
      selectJobLoaded,
      (invoices, jobLoaded) => jobLoaded && invoices.length === 0,
    ),
    transactionsEmpty: createSelector(
      selectTransactions,
      selectJobLoaded,
      (transactions, jobLoaded) => jobLoaded && transactions.length === 0,
    ),
    damagesEmpty: createSelector(
      selectDamages,
      selectJobLoaded,
      (damages, jobLoaded) => {
        return jobLoaded && damages?.length === 0
      },
    ),
    mutationButtonsDisabled: createSelector(
      selectJobInCurrentZone,
      (jobInCurrentZone) => !jobInCurrentZone,
    ),
    job: selectJobTotals,
    transactionActions: createSelector(
      selectTransactions,
      jobToolFeature.selectActionsMenuTransactionsId,
      selectJobInCurrentZone,
      selectJobCollectibles,
      // Requirement: a row action the user may not perform is hidden, not shown
      // and left to fail at the server. Each is hidden by the same permission
      // its own mutation guards on.
      //
      // Requirement: these gate on the permission only, never the zone's
      // payment-type config — an open hold is money already sitting on a
      // customer's card and stays settleable in a zone whose type was since
      // switched off.
      selectMayTakeBlueSageCardPayment,
      selectMayRefundTransaction,
      createCheckSinglePermissionSelector({ permission: 'bluesage.cancelLink' }),
      createCheckSinglePermissionSelector({ permission: 'transactions.delete' }),
      (
        transactions,
        selectedTransactionId,
        jobInCurrentZone,
        takePayment,
        mayCompleteHold,
        mayRefund,
        mayCancelPayLink,
        mayDelete,
      ) => {
        const selectedTransaction = transactions.find((t) => t.id === selectedTransactionId);

        if (!selectedTransaction) {
          return [];
        }

        const artifactReceiptId = selectedTransaction.metadata?.artifactReceiptId;

        const transactionDeleted = Boolean(selectedTransaction.deletedAt);

        // Reads `metadata.refundedBy` — the CLIENT half of the double-refund
        // defence. The server half (K28) is the one that actually holds; this
        // just keeps the operator from meeting a refusal they could have been
        // told about.
        // Requirement: a refund or release answers at once and settles in the
        // background, so while its leg is still `pending` every action that
        // would start another one on this payment is withheld — the server would
        // refuse it, and a second void on a card reader is what this prevents.
        const refundInProgress = blueSageRefundInProgress(transactions, selectedTransaction.id);

        const whyNotRefundable = describeWhyNotRefundable(
          selectedTransaction, jobInCurrentZone, refundInProgress,
        );

        // A hold is a PENDING transaction carrying a preauth reference. Once it
        // is completed the transaction goes `paid` and this disappears, which is
        // right: there is no longer a hold to release, only a payment to refund.
        //
        // An online hold that was completed stays `pending` until BlueSage's
        // event settles it, and is not a hold any more: neither item applies.
        const openPreauthRef = !selectedTransaction.deletedAt
          && selectedTransaction.stage === TransactionStage.Pending
          && !selectedTransaction.metadata?.bluesageCapturedAt
          && selectedTransaction.metadata?.bluesagePreauthRef;

        // A push we sent as an authorization whose confirming event has not
        // landed yet. It looks identical to a hold on screen and it is not one
        // that can be acted on, so the two hold items are SHOWN and disabled
        // with the reason rather than hidden.
        //
        // Hiding them is what made this unfindable during testing: the preauth
        // reference only ever arrives by EVENT — the push's own `202` carries no
        // `transaction_ref` — so while events were being lost the menu offered
        // nothing at all and read as a missing feature.
        const awaitingPreauthRef = !selectedTransaction.deletedAt
          && !openPreauthRef
          && selectedTransaction.stage === TransactionStage.Pending
          && selectedTransaction.metadata?.bluesagePushId
          && selectedTransaction.metadata?.bluesageChannel === 'terminal';

        const holdItemVisible = Boolean(openPreauthRef || awaitingPreauthRef);

        // A completed online hold until `ticket.completed` settles it: the money
        // is being taken, so the row is neither a hold nor deletable.
        const completingHold = !selectedTransaction.deletedAt
          && selectedTransaction.stage === TransactionStage.Pending
          && Boolean(selectedTransaction.metadata?.bluesageCapturedAt);

        // An ONLINE hold's Delete is greyed with the server's reason. Terminal rows
        // keep today's menu: a push still waiting for its event must stay
        // deletable once the server's unsettled window has passed.
        const onlineHold = Boolean(openPreauthRef)
          && selectedTransaction.metadata?.bluesageHold === true;

        const openPayLink = hasOpenBlueSagePayLink(selectedTransaction);

        const canCompleteHold = holdItemVisible && Boolean(takePayment?.enabled);

        const whyNotActionableHold = openPreauthRef
          ? (jobInCurrentZone
            ? (refundInProgress ? BLUESAGE_REFUND_IN_PROGRESS_REASON : undefined)
            : 'This job is in another zone — switch to it to act on this hold.')
          : 'BlueSage has not confirmed this authorization yet. It becomes available '
            + 'as soon as the terminal reports it.';

        return [
          {
            label: 'Edit',
            icon: 'pi pi-pencil',
            command: () => this.store.dispatch(this.actions.editTransactionMenuItemClicked()),
            disabled: transactionDeleted || !jobInCurrentZone,
          },
          {
            label: 'View Receipt',
            icon: 'pi pi-eye',
            disabled: !artifactReceiptId,
            command: () => this.store.dispatch(this.actions.viewReceiptMenuItemClicked()),
          },
          {
            label: 'Delete',
            icon: 'pi pi-trash',
            command: () => this.store.dispatch(this.actions.deleteTransactionMenuItemClicked()),
            visible: Boolean(mayDelete),
            // An online hold is released, not deleted: the server refuses the
            // delete, so it is not offered.
            disabled: transactionDeleted || !jobInCurrentZone || openPayLink || onlineHold
              || completingHold,
            tooltip: completingHold
              ? BLUESAGE_COMPLETING_HOLD_DELETE_REASON
              : (openPayLink
                ? BLUESAGE_OPEN_PAY_LINK_DELETE_REASON
                : (onlineHold ? BLUESAGE_OPEN_HOLD_DELETE_REASON : undefined)),
          },
          {
            // Deleting the transaction does not withdraw the customer-facing URL.
            // This action calls the provider-backed cancellation mutation instead.
            label: 'Cancel pay link',
            icon: 'pi pi-link',
            visible: openPayLink && Boolean(mayCancelPayLink),
            disabled: !jobInCurrentZone,
            tooltip: jobInCurrentZone
              ? undefined
              : 'This job is in another zone — switch to it to act on this link.',
            command: () => this.store.dispatch(
              CancelPayLinkDialogActions.cancelPayLinkFromRowClicked({
                transactionId: selectedTransaction.id,
                amountMinor: Math.abs(selectedTransaction.amount ?? 0),
                payUrl: selectedTransaction.metadata?.bluesagePayUrl,
                hold: selectedTransaction.metadata?.bluesageHold === true,
              }),
            ),
          },
          {
            // An open authorization freezes the customer's money until the issuer
            // expires it. Cancellation is confirmed in its own row-launched dialog.
            //
            // Shown only on a transaction that actually holds one — a pending
            // transaction carrying a preauth reference.
            label: 'Cancel pre-auth',
            icon: 'pi pi-unlock',
            visible: holdItemVisible && mayRefund,
            disabled: Boolean(whyNotActionableHold),
            tooltip: whyNotActionableHold,
            command: () => this.store.dispatch(
              CancelPreauthDialogActions.cancelPreauthFromRowClicked({
                transactionId: selectedTransaction.id,
                amountMinor: Math.abs(selectedTransaction.amount ?? 0),
                terminalEpi: selectedTransaction.metadata?.bluesageTerminalEpi,
              }),
            ),
          },
          {
            // ⚠ There is a MONEY reason completing belongs here rather than on
            // the device: a preauth completed on the terminal produces a
            // standalone-origin transaction and permanently loses partial-refund
            // capability. Completed through a CRM ticket push it stays
            // Connect-origin and keeps partials.
            //
            // It lived only inside the payment dialog, which is not where anyone
            // looks — a hold IS a transaction row, so the action that settles it
            // belongs on that row's menu, beside Release. This is step ONE of the
            // two-step: it opens the confirmation that names the terminal and
            // warns about the ~30 second tip prompt. Nothing is sent from here.
            label: 'Complete authorization',
            icon: 'pi pi-check-square',
            visible: canCompleteHold && mayCompleteHold,
            disabled: Boolean(whyNotActionableHold),
            tooltip: whyNotActionableHold,
            command: () => this.store.dispatch(
              CreateTransactionDialogActions.completeAuthorizationFromRowClicked({
                jobId: takePayment.jobId,
                customerId: takePayment.customerId,
                currency: takePayment.currency,
                // The row names its own hold; the job-wide single-hold guess is
                // wrong once a job carries two.
                openPreauthTransactionId: selectedTransaction.id,
                // Requirement: completing a hold for more than was authorized is
                // a second authorization, not a completion.
                amountMinor: Math.abs(selectedTransaction.amount ?? 0),
              }),
            ),
          },
          {
            // Requirement: NO PERMISSION -> the item is HIDDEN. Has permission but
            // not refundable -> DISABLED with a tooltip saying why. They are two
            // different questions and conflating them is what makes UIs confusing:
            // "you may not do this" and "this cannot be done" need different
            // answers.
            label: 'Refund',
            icon: 'pi pi-replay',
            visible: mayRefund,
            disabled: Boolean(whyNotRefundable),
            tooltip: whyNotRefundable,
            command: () => this.store.dispatch(BlueSageRefundDialogActions.refundMenuItemClicked({
              transactionId: selectedTransaction.id,
              originalMinor: Math.abs(selectedTransaction.amount ?? 0),
              refundableMinor: refundableMinorOf(selectedTransaction),
              channel: selectedTransaction.metadata?.bluesageChannel,
              cardLast4: selectedTransaction.metadata?.bluesageCardLast4,
              // Decides whether the refund MOVES money or merely records one. A
              // payment taken on another system has no BlueSage reference to
              // refund against, so the dialog must say so rather than claim a
              // card was credited.
              provider: selectedTransaction.paymentType?.provider ?? undefined,
              // What the customer was charged for paying by card on this sale.
              // Quoting only — the figure that reaches the card is grossed up
              // server-side from the transaction's own recorded differential.
              // Absent means the location runs no dual pricing, which is zero.
              saleDifferentialMinor:
                Number(selectedTransaction.metadata?.bluesageDifferential ?? 0) || 0,
              // What the card was charged. The settle handler omits this key
              // whenever it equals the credited amount, so absent means "the
              // same as the base" — which is why it falls back to the amount
              // rather than to zero.
              salePaidMinor:
                Number(selectedTransaction.metadata?.bluesageAmountPaid ?? 0)
                  || Math.abs(selectedTransaction.amount ?? 0),
              // Decides whether the "also refund the tip" choice is offered at
              // all. Absent means there was none.
              saleTipMinor: Number(selectedTransaction.metadata?.bluesageTip ?? 0) || 0,
            })),
          },
        ];
      }
    ),
    // Requirement: recording a manual payment needs no card processor and no
    // BlueSage permission, so this payload is ungated — the dialog drops the
    // card tiles instead.
    createTransaction: selectJobCollectibles,
    damageActions: createSelector(
      selectJobInCurrentZone,
      (jobInCurrentZone) => {
        return [
          {
            label: 'Edit',
            icon: 'pi pi-pencil',
            command: () => this.store.dispatch(this.actions.editDamageMenuItemClicked()),
            disabled: !jobInCurrentZone,
          },
          {
            label: 'Delete',
            icon: 'pi pi-trash',
            command: () => this.store.dispatch(this.actions.deleteDamageMenuItemClicked()),
            disabled: !jobInCurrentZone,
          },
        ];
      },
    ),
  }));

  /**
   * Every permission check in this component now reads the branding store
   * through a selector, so the view model is the store's own and needs no
   * stream combined into it.
   *
   * It used to patch Refund's visibility from `PermissionService` out here,
   * because a permission was believed not to be store state. It is: permissions
   * arrive on the branding query and live in `brandingFeature.selectPermissions`,
   * initialised to `[]`, so a selector both recomputes when they load and fails
   * closed until they do.
   *
   * That also retires a real hazard rather than merely tidying one up. The old
   * `combineLatest` needed `startWith(false)` to emit at all, because
   * `PermissionService.watch()` filters out `undefined` and emits nothing until
   * permissions load — and the template root is `*ngIf="viewModel$ | async"`, so
   * a missing seed rendered the entire financials tab blank. A selector always
   * has a value, so there is no seed to forget.
   */
  public viewModel$ = this.storeViewModel$;

  public colorMap: Record<TransactionStage | 'deleted', TagColor> ={
    [TransactionStage.Paid]: { backgroundColor: '#E3FBE3', text: '#0A470A' },
    [TransactionStage.Pending]: { backgroundColor: '#F0F4F8', text: '#32383E' },
    [TransactionStage.Cancelled]: { backgroundColor: '#FCE4E4', text: '#7D1212' },
    deleted: { backgroundColor: '#FCE4E4', text: '#7D1212' },
  }

  /**
   * What the row IS on the BlueSage rail, beside what it was filed as.
   *
   * A template-facing formatter, so it stays out of the selectors: `type` and
   * `paymentType` are already on the row, and this only reads metadata the CRM
   * or an event wrote. Two rows that both read "Deposit (BlueSagePay)" can be a
   * settled sale and an open hold on a customer's card.
   */
  public describeRail = describeBlueSageRail;

  /**
   * Why a BlueSage row is still `Pending` when the card was already approved.
   *
   * Settlement lags the charge by tens of seconds, and a bare "Pending" over
   * money that has demonstrably left a card reads as a failure — which is how
   * an operator ends up taking a second payment.
   */
  public describePending = describeBlueSagePendingReason;

  /**
   * Why a refund leg is sitting at `cancelled`.
   *
   * A cancelled leg means no money moved — the refund path cancels its own leg
   * when the rail refuses, so job totals never count a refund that did not
   * happen. Saying nothing about it leaves a bare red row that reads like a
   * failure nobody explained.
   */
  public describeRefundFailure = describeBlueSageRefundFailure;

  public actions = JobFinancialsActions;

  public paymentActions = CreateTransactionDialogActions;

  constructor(
    public store: Store,
    public router: Router,
    private detailsHelper: DetailsHelperService,
  ) { }

}
